import { isNumeric, numericValue } from './cards';
import type { AbsorbChoice, BuildPile, Card, GameState, PlayerId } from './types';

export function removeCardById(cards: Card[], cardId: string): { next: Card[]; removed: Card } {
  const idx = cards.findIndex((card) => card.id === cardId);
  if (idx < 0) {
    throw new Error(`Card ${cardId} not found.`);
  }
  const next = [...cards];
  const [removed] = next.splice(idx, 1);
  return { next, removed };
}

export function getTopCard(stackCards: Card[]): Card | null {
  if (!stackCards.length) return null;
  return stackCards[stackCards.length - 1];
}

export function sameRoyal(a: Pick<Card, 'rank'>, b: Pick<Card, 'rank'>): boolean {
  return a.rank === b.rank && (a.rank === 'J' || a.rank === 'Q' || a.rank === 'K');
}

export function countHandValue(cards: Card[], value: number): number {
  return cards.filter((card) => isNumeric(card) && numericValue(card) === value).length;
}

export function findSubsetChoices(cards: Card[], targetValue: number): Card[][] {
  const numericCards = cards.filter(isNumeric);
  const picks: Card[][] = [];

  function backtrack(start: number, current: Card[], sum: number): void {
    if (sum === targetValue && current.length) {
      picks.push([...current]);
      return;
    }
    if (sum >= targetValue) {
      return;
    }
    for (let i = start; i < numericCards.length; i += 1) {
      const card = numericCards[i];
      current.push(card);
      backtrack(i + 1, current, sum + numericValue(card));
      current.pop();
    }
  }

  backtrack(0, [], 0);

  const dedupe = new Map<string, Card[]>();
  picks.forEach((pick) => {
    const ids = pick.map((card) => card.id).sort().join('|');
    if (!dedupe.has(ids)) {
      dedupe.set(ids, pick);
    }
  });

  return Array.from(dedupe.values()).sort((a, b) => {
    if (a.length !== b.length) return a.length - b.length;
    return a.map((card) => card.id).join('|').localeCompare(b.map((card) => card.id).join('|'));
  });
}

export function nextPlayerId(players: { id: string }[], currentPlayerId: string): string {
  const currentIdx = players.findIndex((player) => player.id === currentPlayerId);
  if (currentIdx < 0) throw new Error('Current player not found.');
  return players[(currentIdx + 1) % players.length].id;
}

export function playerHasBuild(builds: BuildPile[], playerId: PlayerId): boolean {
  return builds.some((build) => build.ownerPlayerId === playerId);
}

export function isBuildOpen(build: BuildPile): boolean {
  if (build.isSolid) return false;
  const sum = build.cards.reduce((acc, card) => acc + (isNumeric(card) ? numericValue(card) : 0), 0);
  return sum === build.value;
}

export function findTableCardCombinations(tableCards: Card[], playedCard: Card, targetValue: number): string[][] {
  if (!isNumeric(playedCard)) return [];
  
  const playedValue = numericValue(playedCard);
  const neededFromTable = targetValue - playedValue;
  
  if (neededFromTable < 0) return [];
  if (neededFromTable === 0) return [[]]; // No table cards needed, but we'll filter this out in moves.ts
  
  const numericTableCards = tableCards.filter(isNumeric);
  const combinations: string[][] = [];
  
  function backtrack(start: number, current: string[], sum: number): void {
    if (sum === neededFromTable && current.length > 0) {
      combinations.push([...current]);
      return;
    }
    if (sum >= neededFromTable) {
      return;
    }
    for (let i = start; i < numericTableCards.length; i += 1) {
      const card = numericTableCards[i];
      current.push(card.id);
      backtrack(i + 1, current, sum + numericValue(card));
      current.pop();
    }
  }
  
  backtrack(0, [], 0);
  
  // Remove duplicates and sort
  const dedupe = new Map<string, string[]>();
  combinations.forEach((combo) => {
    const key = combo.sort().join('|');
    if (!dedupe.has(key)) {
      dedupe.set(key, combo);
    }
  });
  
  return Array.from(dedupe.values());
}

export function shallowCloneState(state: GameState): GameState {
  return {
    ...state,
    players: [...state.players],
    deck: [...state.deck],
    tableLoose: [...state.tableLoose],
    builds: state.builds.map((pile) => ({ ...pile, cards: [...pile.cards] })),
    stays: state.stays.map((pile) => ({ ...pile, cards: [...pile.cards] })),
    hands: Object.fromEntries(Object.entries(state.hands).map(([k, cards]) => [k, [...cards]])),
    collections: Object.fromEntries(
      Object.entries(state.collections).map(([k, stack]) => [k, { ...stack, cards: [...stack.cards] }])
    ),
    royalPending: Object.fromEntries(Object.entries(state.royalPending).map(([k, cards]) => [k, [...cards]])),
    turnContext: {
      ...state.turnContext,
      lastAction: state.turnContext.lastAction ? { ...state.turnContext.lastAction } : undefined,
      pendingAbsorption: state.turnContext.pendingAbsorption
        ? {
            ...state.turnContext.pendingAbsorption,
            destinationCards: [...state.turnContext.pendingAbsorption.destinationCards],
            choices: state.turnContext.pendingAbsorption.choices.map((choice: AbsorbChoice) => ({
              ...choice,
              tableCardIds: [...choice.tableCardIds],
            })),
          }
        : null,
      log: [...state.turnContext.log],
    },
    meta: {
      ...state.meta,
      scores: { ...state.meta.scores },
      reservedRound2Cards: [...state.meta.reservedRound2Cards],
    },
  };
}
